import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { AxiosError } from "axios";
import { api, sessionStore, tokenStore } from "../api/client";

/** Port of the legacy $_SESSION runtime context (auth-login.php). Never contains DB credentials. */
export interface RuntimeSession {
  UserID: number | string;
  UserName: string | null;
  AccessType: string | number | null;
  DeptID: number | string | null;
  SubDeptID: number | string | null;
  UnitID: number | string | null;
  IsDev: number | string | null;
  ClientID: string | null;
  ClientName: string | null;
  UserRoleID: number | string | null;
  StartYear: string | number | null;
  IncludePath: string;
  ClientPath: string;
  SideBarPath: string;
  ClientConfigData: Record<string, unknown> | null;
}

export interface Validity { Status: "OK" | "WARNING" | "EXPIRED"; DaysLeft: number | null; ExpiryDate: string | null }

export interface LoginResponse { Error: string | null; Msg: string; Token?: string; Session?: RuntimeSession; Validity?: Validity }

interface AuthCtx {
  session: RuntimeSession | null;
  token: string | null;
  loading: boolean;
  login: (username: string, userpassword: string) => Promise<LoginResponse>;
  logout: () => void;
}

const Ctx = createContext<AuthCtx | null>(null);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [token, setToken] = useState<string | null>(tokenStore.get());
  const [session, setSession] = useState<RuntimeSession | null>(sessionStore.get<RuntimeSession>());
  const [loading, setLoading] = useState<boolean>(!!token && !session);

  useEffect(() => {
    if (token && !session) {
      api.get("/auth/session").then((r) => { setSession(r.data.Session); sessionStore.set(r.data.Session); })
        .catch(() => { tokenStore.clear(); setToken(null); })
        .finally(() => setLoading(false));
    }
  }, [token, session]);

  const login = useCallback(async (username: string, userpassword: string) => {
    try {
      const r = await api.post<LoginResponse>("/auth/login", { username, userpassword });
      if (!r.data.Error && r.data.Token && r.data.Session) {
        tokenStore.set(r.data.Token); sessionStore.set(r.data.Session);
        setToken(r.data.Token); setSession(r.data.Session);
      }
      return r.data;
    } catch (err) {
      // Real AdminLoginDetails DB is deferred shared infra in this preview environment (503).
      // Isolated DEV/PREVIEW-ONLY fallback — server-side gated to dev/preview mode only, never
      // touches the real auth path above.
      if ((err as AxiosError)?.response?.status === 503) {
        const r = await api.post<LoginResponse>("/auth/preview-login", { username, userpassword });
        if (!r.data.Error && r.data.Token && r.data.Session) {
          tokenStore.set(r.data.Token); sessionStore.set(r.data.Session);
          setToken(r.data.Token); setSession(r.data.Session);
        }
        return r.data;
      }
      throw err;
    }
  }, []);

  const logout = useCallback(() => {
    api.post("/auth/logout").catch(() => undefined);
    tokenStore.clear(); setToken(null); setSession(null);
  }, []);

  const value = useMemo(() => ({ session, token, loading, login, logout }), [session, token, loading, login, logout]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
};

export function useAuth(): AuthCtx {
  const c = useContext(Ctx);
  if (!c) throw new Error("AuthProvider missing");
  return c;
}
